import React from "react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import { format, parseISO } from "date-fns";
import { ptBR } from "date-fns/locale";
import styles from "./styles.module.scss";

interface TestResult {
  testResultId: string;
  startDate: string;
  endDate: string;
  totalQuestions: number;
  correctAnswers: number;
  incorrectAnswers: number;
  correctAnswerPercentage: number;
  incorrectAnswersPercentage: number;
}

interface TestData {
  totalTests: number;
  totalQuestions: number;
  totalCorrectAnswers: number;
  totalIncorrectAnswers: number;
  correctAnswerPercentage: number;
  testResults: TestResult[];
}

interface LineChartProps {
  data: TestData;
  timeFormat: "day" | "month";
  translate: (key: string) => string;
}

interface ChartDataPoint {
  date: string;
  acertos: number;
  rawDate: Date;
}

const LineChartComponent: React.FC<LineChartProps> = ({
  data,
  timeFormat = "day",
  translate,
}) => {
  // Verifica se há mais de um teste para exibir o gráfico
  if (data.testResults.length <= 1) {
    return null;
  }

  // Process and format data for Recharts
  const chartData: ChartDataPoint[] = data.testResults.map(result => {
    const date = parseISO(result.startDate);
    return {
      date:
        timeFormat === "day"
          ? format(date, "dd/MM/yy", { locale: ptBR })
          : format(date, "MM/yyyy", { locale: ptBR }),
      acertos: result.correctAnswers,
      rawDate: date,
    };
  });

  // Sort by date
  chartData.sort((a, b) => a.rawDate.getTime() - b.rawDate.getTime());

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      return (
        <div className={styles.customTooltip}>
          <p className="label">{`${translate(
            "screenApp_results_examDate",
          )}: ${label}`}</p>
          <p className={styles.tooltipValue}>{`${translate(
            "screenApp_results_hits",
          )}: ${payload[0].value}`}</p>
        </div>
      );
    }
    return null;
  };

  return (
    <div className={styles.container}>
      <div className={styles.chartContainer}>
        <ResponsiveContainer>
          <LineChart
            data={chartData}
            margin={{ top: 20, right: 30, left: 20, bottom: 10 }}
          >
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="date" />
            <YAxis
              label={{
                value: translate("labels_answers_total_hits"),
                angle: -90,
                position: "insideLeft",
              }}
            />
            <Tooltip content={<CustomTooltip />} />
            <Legend verticalAlign="top" height={36} />
            <Line
              type="monotone"
              dataKey="acertos"
              name={translate("labels_tests_evolution")}
              stroke="#C62828"
              strokeWidth={2}
              dot={{ r: 5 }}
              activeDot={{ r: 8 }}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default LineChartComponent;
